import { useEffect } from "react";
import {
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import GradingIcon from "@mui/icons-material/Grading";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectCurrentReadingContributors,
  selectGradingViewUserId,
  selectGradingViewUsername,
  selectIsCurrentUserWorkspaceOwner,
  selectIsEvaluator,
  selectIsGradingViewActive,
  selectSelectedReadingId,
} from "@store/selectors";
import {
  enterGradingView,
  exitGradingView,
  loadReadingContributors,
} from "@store/thunks/workspaceThunks";
import { selectSidebarMode } from "@store/slices/viewSlice";

/**
 * Owner-only header bar above the PDF viewer (Stage 3.7).
 *
 * - Inactive: shows a dropdown of contributors with annotation counts.
 *   Selecting one enters grading view — the reader, sidebar, canvas
 *   references, and grade tab all swap over to that contributor's
 *   highlights.
 * - Active: shows the contributor being viewed, prev/next arrows to
 *   cycle through the list, a dropdown to jump to any contributor,
 *   and an Exit button to return to the owner's own view.
 *
 * Renders nothing for non-owners or when there's no selected reading.
 */
const GradingViewBar = ({ inline = false }: { inline?: boolean }) => {
  const dispatch = useAppDispatch();
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const isEvaluator = useAppSelector(selectIsEvaluator);
  const selectedReadingId = useAppSelector(selectSelectedReadingId);
  const contributors = useAppSelector(selectCurrentReadingContributors);
  const isActive = useAppSelector(selectIsGradingViewActive);
  const currentUserId = useAppSelector(selectGradingViewUserId);
  const currentUsername = useAppSelector(selectGradingViewUsername);
  const sidebarMode = useAppSelector(selectSidebarMode);

  // Refresh the contributor list when the reading switches. Cheap — the
  // result is cached on the reading; subsequent enters/exits use the
  // cache. Owner+evaluator only, so non-evaluator owners don't fetch.
  useEffect(() => {
    if (isOwner && isEvaluator && selectedReadingId) {
      dispatch(loadReadingContributors());
    }
  }, [dispatch, isOwner, isEvaluator, selectedReadingId]);

  if (!isOwner || !isEvaluator || !selectedReadingId) return null;
  // Hide the inactive picker when the sidebar isn't in Evaluate mode.
  // Always show while actively grading — the "viewing X's annotations"
  // banner is critical context that shouldn't depend on sidebar state.
  if (!isActive && sidebarMode !== "evaluate") return null;

  const currentIndex = currentUserId
    ? contributors.findIndex((c) => c.user_id === currentUserId)
    : -1;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < contributors.length - 1;

  const handlePickContributor = (userId: string) => {
    const contributor = contributors.find((c) => c.user_id === userId);
    if (!contributor) return;
    dispatch(
      enterGradingView({ userId: contributor.user_id, username: contributor.username })
    );
  };

  const handleExit = () => {
    dispatch(exitGradingView());
  };

  const handlePrev = () => {
    if (!canGoPrev) return;
    const prev = contributors[currentIndex - 1];
    if (!prev) return;
    dispatch(enterGradingView({ userId: prev.user_id, username: prev.username }));
  };

  const handleNext = () => {
    if (!canGoNext) return;
    const next = contributors[currentIndex + 1];
    if (!next) return;
    dispatch(enterGradingView({ userId: next.user_id, username: next.username }));
  };

  return (
    <Paper
      elevation={inline ? 0 : 2}
      sx={{
        // Floating over the PDF (default), or docked inline in the
        // sidebar's Evaluate panel (inline). Same positioning convention
        // as CanvasEvaluationToolbar when floating: the PDF container is
        // fixed at top:0 behind a ~64px AppBar, so offset to 76.
        ...(inline
          ? { position: "static", width: "100%", mb: 2, flexWrap: "wrap" }
          : { position: "absolute", top: 76, left: 16, zIndex: 10 }),
        px: 1.5,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: 1,
        borderColor: "divider",
        // Subtle accent when active so the owner can tell at a glance
        // they're not looking at their own work.
        borderLeftWidth: isActive ? 4 : 1,
        borderLeftColor: isActive ? "secondary.main" : "divider",
      }}
    >
      <GradingIcon fontSize="small" color={isActive ? "secondary" : "action"} />

      {!isActive ? (
        // Inactive state — empty picker prompting "select to grade"
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="grading-view-picker-label">
            Grade contributor
          </InputLabel>
          <Select
            labelId="grading-view-picker-label"
            label="Grade contributor"
            value=""
            onChange={(e) => handlePickContributor(e.target.value as string)}
            disabled={contributors.length === 0}
          >
            {contributors.length === 0 ? (
              <MenuItem value="" disabled>
                No contributors yet
              </MenuItem>
            ) : (
              contributors.map((c) => (
                <MenuItem key={c.user_id} value={c.user_id}>
                  {c.username}
                  <Chip
                    label={`${c.annotation_count} ann.`}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      ) : (
        // Active state — picker prefilled with current selection,
        // prev/next arrows for quick cycling, exit button on the right.
        <>
          <Tooltip title="Previous contributor">
            <span>
              <IconButton
                size="small"
                onClick={handlePrev}
                disabled={!canGoPrev}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={currentUserId ?? ""}
              onChange={(e) => handlePickContributor(e.target.value as string)}
            >
              {contributors.map((c) => (
                <MenuItem key={c.user_id} value={c.user_id}>
                  {c.username}
                  <Chip
                    label={`${c.annotation_count} ann.`}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Next contributor">
            <span>
              <IconButton
                size="small"
                onClick={handleNext}
                disabled={!canGoNext}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            Viewing {currentUsername} · read-only
          </Typography>
          <Tooltip title="Exit grading view">
            <Button
              size="small"
              variant="text"
              onClick={handleExit}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ ml: 0.5 }}
            >
              Exit
            </Button>
          </Tooltip>
        </>
      )}
    </Paper>
  );
};

export default GradingViewBar;
